import React, { Component } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalFooter,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives';
import {
	AnalyticsSource,
	AnalyticsSubject,
} from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types';
import {
	type IssueContainerName,
	SERVICE_DESK_ISSUE_CONTAINER_NAME,
	PROJECT_ISSUE_CONTAINER_NAME,
} from '@atlassian/jira-common-constants/src/issue-container-names';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { MODAL } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';

export type Props = {
	isDeleting: boolean;
	issueContainerName: IssueContainerName;
	projectName: string;
	onDelete: (analyticsEvent: AnalyticsEvent) => void;
	onClose: () => void;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

type FooterProps = {
	isDeleting: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onDelete: (browserEvent: any, arg2: AnalyticsEvent) => void;
	onClose: () => void;
	intl: IntlShape;
};

const ActionButtonsWrapper = (props: FooterProps) => (
	<Inline space="space.100">
		<LoadingButton
			appearance="danger"
			onClick={props.onDelete}
			isDisabled={props.isDeleting}
			isLoading={props.isDeleting}
		>
			{props.intl.formatMessage(messages.confirmDeleteButton)}
		</LoadingButton>
		<Button appearance="subtle-link" onClick={props.onClose} isDisabled={props.isDeleting}>
			{props.intl.formatMessage(messages.cancelDeleteButton)}
		</Button>
	</Inline>
);

export const Footer = (props: FooterProps) => ActionButtonsWrapper(props);

// eslint-disable-next-line jira/react/no-class-components
export class DeleteProjectModal extends Component<PropsWithIntl> {
	onDialogDismissed = () => {
		const { isDeleting, onClose } = this.props;
		!isDeleting && onClose();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onDelete = (_: any, analyticsEvent: AnalyticsEvent) => {
		this.props.onDelete(analyticsEvent);
	};

	getBackupLink() {
		return (
			<a
				target="_blank"
				href="https://confluence.atlassian.com/adminjiracloud/exporting-issues-776636787.html"
			>
				{this.props.intl.formatMessage(messages.backupCallToActionPart2)}
			</a>
		);
	}

	render() {
		const {
			projectName,
			intl: { formatMessage },
			issueContainerName,
		} = this.props;

		return (
			<ModalTransition>
				<ModalDialog onClose={this.onDialogDismissed} width={gridSize * 50} autoFocus={false}>
					<ModalHeader>
						<ModalTitle appearance="danger">
							{formatMessage(messages.modalHeader, { projectName })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<ViewTracker />
						<p>
							{formatMessage(
								issueContainerName === SERVICE_DESK_ISSUE_CONTAINER_NAME
									? messages.explainWhatDeleteServiceProjectDoes
									: messages.explainWhatDeleteDoes,
							)}
						</p>
						<p>
							{formatMessage(messages.backupCallToActionPart1)} {this.getBackupLink()}{' '}
							{formatMessage(messages.backupCallToActionPart3)}
						</p>
						<p>{formatMessage(messages.cannotBeUndone)}</p>
					</ModalBody>
					<ModalFooter>
						<Footer
							onDelete={this.onDelete}
							onClose={this.props.onClose}
							isDeleting={this.props.isDeleting}
							intl={this.props.intl}
						/>
					</ModalFooter>
				</ModalDialog>
			</ModalTransition>
		);
	}
}

export default AnalyticsSource(
	'deleteProject',
	MODAL,
)(AnalyticsSubject('project')(injectIntl(DeleteProjectModal)));

export const defaultProps = {
	issueContainerName: PROJECT_ISSUE_CONTAINER_NAME,
} as const;
